import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share, PlusSquare, X, Smartphone } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";

const PWAInstallPrompt = () => {
  const { canInstall, canPrompt, isIOSLike, installed, promptInstall, markDismissed, recentlyDismissed, platform } =
    usePWAInstall();
  const [bannerOpen, setBannerOpen] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (installed || !canInstall) return;
    if (recentlyDismissed()) return;
    const t = setTimeout(() => setBannerOpen(true), 6000);
    return () => clearTimeout(t);
  }, [installed, canInstall, recentlyDismissed]);

  const handleInstall = async () => {
    if (canPrompt) {
      await promptInstall("banner");
      setBannerOpen(false);
    } else if (isIOSLike) {
      setIosOpen(true);
      setBannerOpen(false);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setBannerOpen(false);
  };

  if (installed) return null;

  return (
    <>
      {bannerOpen && canInstall && (
        <div className="fixed bottom-4 left-4 right-4 z-[70] mx-auto max-w-md rounded-2xl border-2 border-primary/20 bg-card shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-3 p-4">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">Installer KelasAI</p>
              <p className="text-xs text-muted-foreground truncate">
                {isIOSLike ? "Ajoute l'app à ton écran d'accueil" : "Accès rapide depuis ton écran d'accueil"}
              </p>
            </div>
            <Button size="sm" onClick={handleInstall} className="gap-1.5 font-bold">
              <Download className="w-4 h-4" />
              Installer
            </Button>
            <button
              onClick={handleDismiss}
              aria-label="Fermer"
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" />
              Installer KelasAI sur {platform === "ipados" ? "iPad" : "iPhone"}
            </DialogTitle>
            <DialogDescription>
              Safari permet d'ajouter KelasAI à ton écran d'accueil en deux étapes.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-4 py-2">
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center">1</span>
              <div className="flex-1">
                <p className="font-semibold text-sm">Appuie sur le bouton Partager</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Share className="w-4 h-4 text-primary" />
                  En bas de Safari (ou en haut sur iPad)
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center">2</span>
              <div className="flex-1">
                <p className="font-semibold text-sm">Choisis « Sur l'écran d'accueil »</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <PlusSquare className="w-4 h-4 text-primary" />
                  Puis confirme avec « Ajouter »
                </p>
              </div>
            </li>
          </ol>
          <div className="rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
            ⚠️ Cette fonctionnalité est uniquement disponible dans Safari. Si tu utilises Chrome ou un autre navigateur sur iOS, ouvre d'abord la page dans Safari.
          </div>
          <Button onClick={() => setIosOpen(false)} className="w-full font-bold">
            J'ai compris
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PWAInstallPrompt;